import { NetworkError, ResponseError } from 'libs/features-api';

const API_BASE_PATH = '/api/v1/line';
const MEMBER_POINT_CARD_INQUIRY_PATH =
  'smart_receipt/users/current/member_point_card_inquiries';
const POINT_CARD_LINK_PATH = 'smart_receipt/users/current/point_card_links';

type ConstructorParams = {
  readonly packageHost: string;
  readonly tenantCode: string;
  readonly liffIdToken: string;
};

export type PointCardLinkParams = {
  readonly accountId: string;
  readonly plainPassword: string;
};

type PointCardLinkPayload = {
  readonly account_id: string;
  readonly plain_password: string;
};

/**
 * digiclue-packageスマレシ専用APIクライアント
 *
 * NOTE: マジプラプロジェクトが控えているので、汎用性は捨てている
 */
export class ApiClient {
  private readonly packageHost: string;
  private readonly liffIdToken: string;
  private readonly memberPointCardInquiryPath: string;
  private readonly pointCardLinkPath: string;

  constructor({ packageHost, tenantCode, liffIdToken }: ConstructorParams) {
    this.packageHost = packageHost;
    this.liffIdToken = liffIdToken;
    this.memberPointCardInquiryPath = `${API_BASE_PATH}/${tenantCode}/${MEMBER_POINT_CARD_INQUIRY_PATH}`;
    this.pointCardLinkPath = `${API_BASE_PATH}/${tenantCode}/${POINT_CARD_LINK_PATH}`;
  }

  /**
   * ユーザーがスマートレシートに連携済みかどうかを問い合わせる
   *
   * NOTE: LINEのトークンIDを元にサーバ側で会員番号をマップしているので、本APIに会員番号は不要
   *
   * @return {Promise<boolean>} 連携済みの場合はtrue、未連携の場合はfalse
   * @throws {import('libs/features-api').NetworkError} FetchAPIが失敗した場合
   * @throws {import('libs/features-api').ResponseError} APIの問い合わせ結果が「失敗」の場合
   */
  async callMemberPointCardInquiry(): Promise<boolean> {
    const url = new URL(this.memberPointCardInquiryPath, this.packageHost);
    const headers = this.buildHeaders();

    const response = await this.invokeGetRequest(url, headers);
    if (!response.ok) {
      throw new ResponseError(response);
    }
    const json = await response.json().catch(() => {
      throw new ResponseError(response);
    });
    if (json.status !== 'succeeded' || json.data.member_flag === undefined) {
      throw new ResponseError(response);
    }
    return json.data.member_flag;
  }

  /**
   * スマートレシートの連携を行う
   *
   * NOTE: LINEのトークンIDを元にサーバ側で会員番号をマップしているので、本APIに会員番号は不要
   *
   * 【レスポンスについて】
   * - 200 : 連携に成功した場合
   * - 400 : 入力項目に不備がある場合
   * - 404 : 設定上通常の遷移ではありえない
   * - 500 : Packageでエラーが発生した場合
   * - 503 : スマレシAPIでエラーが発生した場合
   *
   * @return {Promise<boolean>} 連携に成功した場合はtrue。入力項目に問題がある場合はfalse
   * @throws {import('libs/features-api').NetworkError} FetchAPIが失敗した場合
   * @throws {import('libs/features-api').ResponseError} APIの問い合わせ結果が「失敗」の場合
   */
  async callPointCardLinkUrl({
    accountId,
    plainPassword
  }: PointCardLinkParams): Promise<boolean> {
    const url = new URL(this.pointCardLinkPath, this.packageHost);
    const headers = this.buildHeaders();
    const payload: PointCardLinkPayload = {
      account_id: accountId,
      plain_password: plainPassword
    };

    const response = await this.invokePostRequest(url, headers, payload);
    if (response.ok) {
      return true;
    }
    if (response.status === 400) {
      return false;
    }
    throw new ResponseError(response);
  }

  private buildHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json; charset=UTF-8',
      'Token-Id': this.liffIdToken
    };
  }

  private async invokeGetRequest(
    url: URL,
    headers: Record<string, string>
  ): Promise<Response> {
    try {
      return await window.fetch(url, { method: 'GET', headers });
    } catch (error) {
      throw new NetworkError(url.toString(), error);
    }
  }

  private async invokePostRequest(
    url: URL,
    headers: Record<string, string>,
    payload: PointCardLinkPayload
  ): Promise<Response> {
    try {
      return await window.fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
    } catch (error) {
      throw new NetworkError(url.toString(), error);
    }
  }
}
